<script setup lang="ts">
import { computed } from "vue";
import BlockListItem from "../BlockListItem.vue";
import AssetLibraryItem from "../AssetLibraryItem.vue";
import DrBlockCell from "./DrBlockCell.vue";
import type {
  ObjectCategory,
  ObjectsByCategory,
} from "@/composable/useObjectsCatalog";

/**
 * Éléments d'une catégorie de l'onglet Objets, chacun dans sa forme : liste de
 * noms pour les blocs de la légende et du dessin, vignettes pour les blocs
 * Inspexio et les parties d'ouvrage.
 */
const props = defineProps<{
  category: ObjectCategory;
  objects: ObjectsByCategory;
  /** Aperçu : nombre maximal d'éléments affichés (tous si absent). */
  limit?: number;
  isArmed: (name: string) => boolean;
}>();

const emit = defineEmits<{
  (e: "pick", category: ObjectCategory, name: string): void;
}>();

/**
 * Aperçu : les premiers éléments — et le bloc armé s'il est plus loin, à la
 * place du dernier, pour qu'il reste visible et surligné (le toucher à nouveau
 * rouvre ses attributs).
 */
function preview<T>(items: T[], nameOf?: (item: T) => string): T[] {
  const limit = props.limit;
  if (limit === undefined || items.length <= limit) return items;
  const shown = items.slice(0, limit);
  const armed = nameOf
    ? items.slice(limit).find((item) => props.isArmed(nameOf(item)))
    : undefined;
  return armed ? [...shown.slice(0, limit - 1), armed] : shown;
}

const byName = (item: { name: string }) => item.name;

const legend = computed(() => preview(props.objects.legend, byName));
const drawing = computed(() => preview(props.objects.drawing, byName));
const dr = computed(() => preview(props.objects.dr, byName));
// Les parties d'ouvrage s'insèrent directement : rien d'armé.
const assets = computed(() => preview(props.objects.assets));
</script>

<template>
  <template v-if="category === 'legend'">
    <BlockListItem
      v-for="block in legend"
      :key="block.name"
      :block="block"
      :active="isArmed(block.name)"
      @select="emit('pick', 'legend', block.name)"
    />
  </template>

  <template v-else-if="category === 'drawing'">
    <BlockListItem
      v-for="block in drawing"
      :key="'dwg-' + block.name"
      :block="block"
      :active="isArmed(block.name)"
      @select="emit('pick', 'drawing', block.name)"
    />
  </template>

  <div v-else-if="category === 'dr'" class="objects-grid dr-grid">
    <DrBlockCell
      v-for="block in dr"
      :key="block.name"
      :block="block"
      :active="isArmed(block.name)"
      @select="emit('pick', 'dr', block.name)"
    />
  </div>

  <div v-else class="objects-grid assets-grid">
    <AssetLibraryItem v-for="asset in assets" :key="asset.id" :asset="asset" />
  </div>
</template>

<style lang="scss" scoped>
// Colonnes selon la largeur du volet (redimensionnable).
.objects-grid {
  display: grid;
  gap: 6px;
}

.dr-grid {
  grid-template-columns: repeat(auto-fill, minmax(72px, 1fr));
}

// Cartes étirées à la hauteur de la rangée : schémas et noms alignés.
.assets-grid {
  grid-template-columns: repeat(auto-fill, minmax(84px, 1fr));
  align-items: stretch;
}
</style>
